import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { motion } from 'framer-motion';
import { ChapterFlowOption } from '../types';
import {
  getGraphQLClient,
  GENERATE_CHAPTER_FLOW_OPTIONS,
  GENERATE_CHAPTER_COVERS,
  APPLY_CHAPTER_FLOW,
  RESET_BOOK_STRUCTURE,
  UPDATE_CHAPTER,
  UPDATE_SCENE,
  CREATE_CHAPTER,
  DELETE_CHAPTER,
  REORDER_CHAPTERS,
  CREATE_SCENE,
  DELETE_SCENE,
  REORDER_SCENES,
  GENERATE_INSTRUCTION_SUGGESTIONS,
  GET_BOOK_HEADER,
  SAVE_SCENE_PLAN_POINT,
  DELETE_SCENE_PLAN_POINT,
  GENERATE_SCENE_PLAN_POINT_SUGGESTIONS,
} from '../lib/graphql';
import { captureException } from '../lib/sentry';
import {
  Button,
  Card,
  Spinner,
  AiEnhancedTextarea,
  Input,
  DropdownMenu,
  DropdownMenuItem,
  ConfirmDialog,
  Modal,
} from '../components/common';
import useAiSuggestions from '../hooks/useAiSuggestions';

const SUGGESTION_DELIMITER = '||';

type ParsedSuggestion = { handle: string | null; body: string };

const parseSuggestion = (raw: string): ParsedSuggestion => {
  const idx = raw.indexOf(SUGGESTION_DELIMITER);
  if (idx === -1) return { handle: null, body: raw.trim() };
  const handle = raw.slice(0, idx).trim();
  const body = raw.slice(idx + SUGGESTION_DELIMITER.length).trim();
  return { handle: handle.length > 0 ? handle : null, body };
};

type GenerateResponse = {
  generateChapterFlowOptions: {
    success: boolean;
    errors: string[];
    options: ChapterFlowOption[] | null;
  };
};

type GenerateCoversResponse = {
  generateChapterCovers: {
    success: boolean;
    errors: string[];
    covers: string[] | null;
  };
};

type ApplyResponse = {
  applyChapterFlow: {
    success: boolean;
    errors: string[];
    book: { id: string; chapters: ServerChapter[] } | null;
  };
};

type ResetResponse = {
  resetBookStructure: {
    success: boolean;
    errors: string[];
  };
};

type ServerPlanPoint = {
  id: string;
  content: string;
  position: number;
};
type ServerScene = {
  id: string;
  title: string;
  description: string | null;
  position: number;
  planPoints?: ServerPlanPoint[];
};
type ServerChapter = {
  id: string;
  title: string;
  position: number;
  scenes: ServerScene[];
};
type BookHeaderResponse = {
  book: {
    id: string;
    title: string;
    structureLocked: boolean;
    chapters: ServerChapter[];
  } | null;
};

// One key-moment under a scene. id is null while it is a local draft; once
// saved, id holds the ScenePlanPoint record id.
type PlanPoint = { id: string | null; text: string };

// One cover bullet in component state. id is null while the bullet is a
// local draft; once saved as a Scene, id holds the scene id so future text
// edits PATCH and removals DELETE the right record.
type Cover = { id: string | null; text: string; scenePlan: PlanPoint[] };

type HydratedChapter = {
  chapterId: string | null;
  title: string;
  description: string;
  covers: Cover[];
};

const hydrateChaptersFromServer = (serverChapters: ServerChapter[]): HydratedChapter[] =>
  [...serverChapters]
    .sort((a, b) => a.position - b.position)
    .map((c) => {
      const covers: Cover[] = [...c.scenes]
        .sort((a, b) => a.position - b.position)
        .map((scene) => ({
          id: scene.id,
          text: scene.title,
          scenePlan: [...(scene.planPoints ?? [])]
            .sort((a, b) => a.position - b.position)
            .map((km) => ({ id: km.id, text: km.content })),
        }));
      return {
        chapterId: c.id,
        title: c.title,
        description: '',
        covers,
      };
    });

type Preset = { id: 'short' | 'standard' | 'long'; min: number; max: number };

const PRESETS: Preset[] = [
  { id: 'short', min: 4, max: 6 },
  { id: 'standard', min: 8, max: 12 },
  { id: 'long', min: 14, max: 20 },
];

const DEFAULT_PRESET_ID: Preset['id'] = 'standard';
const COUNT_FLOOR = 1;
const COUNT_CEILING = 60;
const COUNT_DRIFT = 2;

const presetMidpoint = (p: Preset): number => Math.round((p.min + p.max) / 2);

const emptyChapter = (): HydratedChapter => ({
  chapterId: null,
  title: '',
  description: '',
  covers: [],
});

export default function ChapterFlowSelectionPage() {
  const { t } = useTranslation();
  const { bookId } = useParams<{ bookId: string }>();
  const navigate = useNavigate();

  const initialPreset = PRESETS.find((p) => p.id === DEFAULT_PRESET_ID)!;

  const [selectedPresetId, setSelectedPresetId] = useState<Preset['id'] | 'manual'>(
    DEFAULT_PRESET_ID
  );
  const [manualOpen, setManualOpen] = useState(false);
  const [targetChapters, setTargetChapters] = useState<number>(presetMidpoint(initialPreset));
  const [minChapters, setMinChapters] = useState<number>(initialPreset.min);
  const [maxChapters, setMaxChapters] = useState<number>(initialPreset.max);

  const [brief, setBrief] = useState('');
  const [bookTitle, setBookTitle] = useState<string | null>(null);

  const [chapters, setChapters] = useState<HydratedChapter[] | null>(null);
  const [editingTitleIndex, setEditingTitleIndex] = useState<number | null>(null);
  const [suggestingForIndex, setSuggestingForIndex] = useState<number | null>(null);

  // True when chapters in state mirror chapters persisted on the backend.
  // Auto-commit on Generate keeps this in sync — refresh hydrates from the server.
  const [committed, setCommitted] = useState(false);
  // Once true, the structure cannot be edited or regenerated from this page.
  const [structureLocked, setStructureLocked] = useState(false);

  const [settingsOpen, setSettingsOpen] = useState(true);
  const [showBriefSuggestions, setShowBriefSuggestions] = useState(false);

  const [isGenerating, setIsGenerating] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);

  const [confirmRegenerateOpen, setConfirmRegenerateOpen] = useState(false);

  const [isContinuing, setIsContinuing] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);

  // Scene-suggestor modal state. Index of the chapter whose modal is open
  // (or null if closed); the suggestions cached per chapter so the user can
  // re-open without losing the list.
  const [sceneSuggestionsModalIdx, setSceneSuggestionsModalIdx] = useState<number | null>(null);
  const [sceneSuggestionsByIdx, setSceneSuggestionsByIdx] = useState<Record<number, string[]>>({});
  const [sceneSuggestionsLoading, setSceneSuggestionsLoading] = useState(false);
  const [sceneSuggestionsError, setSceneSuggestionsError] = useState<string | null>(null);

  // Three AI-generated ToC options the user picks from. Null while we haven't
  // generated anything (or after the user picked one and we've applied it).
  const [options, setOptions] = useState<ChapterFlowOption[] | null>(null);
  const [expandedOptionId, setExpandedOptionId] = useState<string | null>(null);
  const [applyingOptionId, setApplyingOptionId] = useState<string | null>(null);

  // Scene-level expansion: which scenes show their key-moment list inline.
  // Keyed by scene id (real, server-assigned). Locally-drafted scenes (no id
  // yet) can't be expanded — they need to be saved first.
  const [expandedSceneIds, setExpandedSceneIds] = useState<Set<string>>(new Set());

  // AI key-moment proposals per scene. Ghost rows the user can accept (saves a
  // real key moment) or dismiss (removes the ghost). Mirrors the discovery-mode
  // pattern from the interview.
  const [proposalsBySceneId, setProposalsBySceneId] = useState<Record<string, string[]>>({});
  const [proposingForSceneId, setProposingForSceneId] = useState<string | null>(null);
  const [dismissedProposals, setDismissedProposals] = useState<Set<string>>(new Set());

  const briefSuggestionVariables = useMemo(
    () => ({ bookId: bookId || '', field: 'chapter_flow_brief' }),
    [bookId]
  );
  const {
    suggestions: briefSuggestions,
    isLoading: briefSuggestionsLoading,
    refresh: refreshBriefSuggestions,
  } = useAiSuggestions({
    fetchMutation: GENERATE_INSTRUCTION_SUGGESTIONS,
    fetchVariables: briefSuggestionVariables,
    responseField: 'generateInstructionSuggestions',
    displayCount: 3,
    fetchCount: 10,
    enabled: showBriefSuggestions && !!bookId,
  });

  useEffect(() => {
    if (!bookId) return;
    let cancelled = false;
    (async () => {
      try {
        const client = getGraphQLClient();
        const data = await client.request<BookHeaderResponse>(GET_BOOK_HEADER, { id: bookId });
        if (cancelled || !data.book) return;
        // Once chapters are approved, the wizard is sealed: structure planning
        // lives in the outline (refine workspace) and re-rolling is no longer
        // offered. Redirect any fresh visit/back-navigation there.
        if (data.book.chapters.length > 0) {
          navigate(`/books/${bookId}/refine`, { replace: true });
          return;
        }
        setBookTitle(data.book.title);
        setStructureLocked(data.book.structureLocked);
      } catch (err) {
        captureException(err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [bookId, navigate]);

  const briefFilled = brief.trim().length > 0;
  const hasChapters = chapters !== null;

  const pickPreset = (preset: Preset) => {
    setSelectedPresetId(preset.id);
    setTargetChapters(presetMidpoint(preset));
    setMinChapters(preset.min);
    setMaxChapters(preset.max);
    setManualOpen(false);
  };

  const clampCount = (value: number): number =>
    Math.max(COUNT_FLOOR, Math.min(COUNT_CEILING, Math.round(value)));

  const handleTargetChange = (raw: string) => {
    const n = parseInt(raw, 10);
    if (Number.isNaN(n)) return;
    const next = clampCount(n);
    setTargetChapters(next);
    setMinChapters(Math.max(COUNT_FLOOR, next - COUNT_DRIFT));
    setMaxChapters(Math.min(COUNT_CEILING, next + COUNT_DRIFT));
    setSelectedPresetId('manual');
  };

  const runGenerateOptions = async () => {
    if (!bookId || !briefFilled) return;
    const client = getGraphQLClient();

    const genData = await client.request<GenerateResponse>(GENERATE_CHAPTER_FLOW_OPTIONS, {
      bookId,
      minChapters,
      maxChapters,
      instructions: brief.trim() || null,
    });
    const genResult = genData.generateChapterFlowOptions;
    if (!genResult.success || !genResult.options) {
      throw new Error(genResult.errors.join(', ') || t('common.error'));
    }
    setOptions(genResult.options);
    setExpandedOptionId(null);
    setSettingsOpen(false);
  };

  const applyOption = async (option: ChapterFlowOption) => {
    if (!bookId) return;
    setApplyingOptionId(option.id);
    setApplyError(null);
    try {
      const client = getGraphQLClient();
      const applyData = await client.request<ApplyResponse>(APPLY_CHAPTER_FLOW, {
        bookId,
        chapters: option.chapters.map((c) => ({
          title: c.title,
          description: '',
          covers: c.covers.map((s) => s.trim()).filter((s) => s.length > 0),
        })),
      });
      const applyResult = applyData.applyChapterFlow;
      if (!applyResult.success || !applyResult.book) {
        throw new Error(applyResult.errors.join(', ') || t('common.error'));
      }
      setChapters(hydrateChaptersFromServer(applyResult.book.chapters));
      setCommitted(true);
      setEditingTitleIndex(null);
      setOptions(null);
      setExpandedOptionId(null);
    } catch (err) {
      captureException(err);
      setApplyError(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setApplyingOptionId(null);
    }
  };

  const generate = async () => {
    if (!bookId || !briefFilled || structureLocked) return;
    // Regenerating an already-committed flow is destructive — make the user confirm.
    if (committed) {
      setConfirmRegenerateOpen(true);
      return;
    }
    setIsGenerating(true);
    setGenerateError(null);
    try {
      await runGenerateOptions();
    } catch (err) {
      captureException(err);
      setGenerateError(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setIsGenerating(false);
    }
  };

  const confirmRegenerate = async () => {
    if (!bookId) return;
    setIsGenerating(true);
    setGenerateError(null);
    try {
      const client = getGraphQLClient();
      const resetData = await client.request<ResetResponse>(RESET_BOOK_STRUCTURE, { bookId });
      const resetResult = resetData.resetBookStructure;
      if (!resetResult.success) {
        throw new Error(resetResult.errors.join(', ') || t('common.error'));
      }
      // After wipe, drop the previously-committed local view so the picker
      // can show — runGenerateOptions only renders when chapters are absent.
      setChapters(null);
      setCommitted(false);
      await runGenerateOptions();
    } catch (err) {
      captureException(err);
      setGenerateError(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setConfirmRegenerateOpen(false);
      setIsGenerating(false);
    }
  };

  const handleBriefQuickAction = () => {
    setShowBriefSuggestions((prev) => !prev);
  };

  const handleBriefSuggestionSelect = (suggestion: string) => {
    const { body } = parseSuggestion(suggestion);
    setBrief(body);
    setShowBriefSuggestions(false);
  };

  const updateChapter = (idx: number, patch: Partial<HydratedChapter>) =>
    setChapters((prev) => (prev ? prev.map((c, i) => (i === idx ? { ...c, ...patch } : c)) : prev));

  const updateChapterTitle = (idx: number, title: string) => updateChapter(idx, { title });

  const updateCovers = (idx: number, covers: Cover[]) =>
    setChapters((prev) => (prev ? prev.map((c, i) => (i === idx ? { ...c, covers } : c)) : prev));

  const setCoverIdAt = (chapterIdx: number, coverIdx: number, sceneId: string) =>
    setChapters((prev) => {
      if (!prev) return prev;
      return prev.map((c, i) => {
        if (i !== chapterIdx) return c;
        const next = c.covers.map((cv, ci) => (ci === coverIdx ? { ...cv, id: sceneId } : cv));
        return { ...c, covers: next };
      });
    });

  // Persist a chapter's title on blur. Updates if the chapter already has
  // an id, otherwise CREATEs it on the backend and stores the new id so
  // subsequent edits patch the same record. A new chapter inserted in the
  // middle of the list is reordered after creation so positions match.
  const persistChapterTitle = async (idx: number) => {
    const chapter = chapters?.[idx];
    if (!chapter || structureLocked || !bookId) return;
    const title = chapter.title.trim();
    if (!title) return;
    try {
      const client = getGraphQLClient();
      if (chapter.chapterId) {
        await client.request(UPDATE_CHAPTER, {
          chapterId: chapter.chapterId,
          title,
        });
      } else {
        const res = await client.request<{
          createChapter: {
            success: boolean;
            errors: string[];
            chapter: { id: string; position: number } | null;
          };
        }>(CREATE_CHAPTER, { bookId, title, description: '' });
        const newId = res.createChapter?.chapter?.id;
        if (!newId) return;
        // Record the new id on the local chapter, then snapshot the
        // resulting order and reorder if the new chapter sits anywhere
        // other than at the end (CREATE_CHAPTER appends server-side).
        const snapshot: HydratedChapter[] = (chapters ?? []).map((c, i) =>
          i === idx ? { ...c, chapterId: newId } : c
        );
        setChapters(snapshot);
        if (idx !== snapshot.length - 1) {
          persistChapterOrder(snapshot);
        }
      }
    } catch (err) {
      captureException(err);
    }
  };

  // Persist a single bullet. Pre-commit chapters skip this — applyChapterFlow
  // will pick up the typed bullets in its payload. Post-commit: a bullet with
  // no id is CREATE_SCENE; with an id, UPDATE_SCENE on the matching scene.
  const persistCoverAt = async (chapterIdx: number, coverIdx: number) => {
    const chapter = chapters?.[chapterIdx];
    if (!chapter || !chapter.chapterId || structureLocked) return;
    const cover = chapter.covers[coverIdx];
    if (!cover) return;
    const text = cover.text.trim();
    if (text.length === 0) return;

    try {
      const client = getGraphQLClient();
      if (cover.id) {
        await client.request(UPDATE_SCENE, { sceneId: cover.id, title: text });
      } else {
        const res = await client.request<{
          createScene: { success: boolean; errors: string[]; scene: { id: string } | null };
        }>(CREATE_SCENE, {
          chapterId: chapter.chapterId,
          title: text,
          position: coverIdx,
        });
        const newId = res.createScene?.scene?.id;
        if (newId) setCoverIdAt(chapterIdx, coverIdx, newId);
      }
    } catch (err) {
      captureException(err);
    }
  };

  const updateCoverAt = (chapterIdx: number, coverIdx: number, value: string) => {
    const chapter = chapters?.[chapterIdx];
    if (!chapter) return;
    const next = chapter.covers.map((cv, i) => (i === coverIdx ? { ...cv, text: value } : cv));
    updateCovers(chapterIdx, next);
  };

  const removeCoverAt = (chapterIdx: number, coverIdx: number) => {
    const chapter = chapters?.[chapterIdx];
    if (!chapter) return;
    const removed = chapter.covers[coverIdx];
    updateCovers(
      chapterIdx,
      chapter.covers.filter((_, i) => i !== coverIdx)
    );
    if (removed?.id && chapter.chapterId && !structureLocked) {
      const client = getGraphQLClient();
      client.request(DELETE_SCENE, { sceneId: removed.id }).catch(captureException);
    }
  };

  const addCoverAt = (chapterIdx: number) => {
    const chapter = chapters?.[chapterIdx];
    if (!chapter) return;
    updateCovers(chapterIdx, [...chapter.covers, { id: null, text: '', scenePlan: [] }]);
  };

  const persistSceneOrder = (chapter: HydratedChapter) => {
    if (!chapter.chapterId || structureLocked) return;
    const ids = chapter.covers.map((c) => c.id).filter((id): id is string => !!id);
    if (ids.length !== chapter.covers.length) return;
    const client = getGraphQLClient();
    client
      .request(REORDER_SCENES, { chapterId: chapter.chapterId, sceneIds: ids })
      .catch(captureException);
  };

  const moveCoverAt = (chapterIdx: number, coverIdx: number, dir: -1 | 1) => {
    setChapters((prev) => {
      if (!prev) return prev;
      const chapter = prev[chapterIdx];
      if (!chapter) return prev;
      const target = coverIdx + dir;
      if (target < 0 || target >= chapter.covers.length) return prev;
      const nextCovers = [...chapter.covers];
      [nextCovers[coverIdx], nextCovers[target]] = [nextCovers[target], nextCovers[coverIdx]];
      const nextChapter = { ...chapter, covers: nextCovers };
      persistSceneOrder(nextChapter);
      return prev.map((c, i) => (i === chapterIdx ? nextChapter : c));
    });
  };

  // ---------- Key moments (per scene) ----------

  const toggleSceneExpanded = (sceneId: string) => {
    setExpandedSceneIds((prev) => {
      const next = new Set(prev);
      if (next.has(sceneId)) next.delete(sceneId);
      else next.add(sceneId);
      return next;
    });
  };

  const updateScenePlanAt = (chapterIdx: number, coverIdx: number, scenePlan: PlanPoint[]) =>
    setChapters((prev) => {
      if (!prev) return prev;
      return prev.map((c, i) => {
        if (i !== chapterIdx) return c;
        const nextCovers = c.covers.map((cv, ci) => (ci === coverIdx ? { ...cv, scenePlan } : cv));
        return { ...c, covers: nextCovers };
      });
    });

  const setPlanPointIdAt = (chapterIdx: number, coverIdx: number, kmIdx: number, kmId: string) =>
    setChapters((prev) => {
      if (!prev) return prev;
      return prev.map((c, i) => {
        if (i !== chapterIdx) return c;
        const nextCovers = c.covers.map((cv, ci) => {
          if (ci !== coverIdx) return cv;
          const nextKms = cv.scenePlan.map((km, ki) => (ki === kmIdx ? { ...km, id: kmId } : km));
          return { ...cv, scenePlan: nextKms };
        });
        return { ...c, covers: nextCovers };
      });
    });

  const updatePlanPointAt = (
    chapterIdx: number,
    coverIdx: number,
    kmIdx: number,
    value: string
  ) => {
    const cover = chapters?.[chapterIdx]?.covers[coverIdx];
    if (!cover) return;
    const next = cover.scenePlan.map((km, i) => (i === kmIdx ? { ...km, text: value } : km));
    updateScenePlanAt(chapterIdx, coverIdx, next);
  };

  const addPlanPointAt = (chapterIdx: number, coverIdx: number) => {
    const cover = chapters?.[chapterIdx]?.covers[coverIdx];
    if (!cover) return;
    updateScenePlanAt(chapterIdx, coverIdx, [...cover.scenePlan, { id: null, text: '' }]);
  };

  const removePlanPointAt = (chapterIdx: number, coverIdx: number, kmIdx: number) => {
    const cover = chapters?.[chapterIdx]?.covers[coverIdx];
    if (!cover) return;
    const removed = cover.scenePlan[kmIdx];
    updateScenePlanAt(
      chapterIdx,
      coverIdx,
      cover.scenePlan.filter((_, i) => i !== kmIdx)
    );
    if (removed?.id && !structureLocked) {
      const client = getGraphQLClient();
      client.request(DELETE_SCENE_PLAN_POINT, { planPointId: removed.id }).catch(captureException);
    }
  };

  // Persist a single key moment. New rows (no id) are created; existing rows
  // are updated. Requires the parent scene to already have a real id.
  const persistPlanPointAt = async (chapterIdx: number, coverIdx: number, kmIdx: number) => {
    if (structureLocked) return;
    const cover = chapters?.[chapterIdx]?.covers[coverIdx];
    if (!cover || !cover.id) return;
    const km = cover.scenePlan[kmIdx];
    if (!km) return;
    const text = km.text.trim();
    if (text.length === 0) return;

    try {
      const client = getGraphQLClient();
      const res = await client.request<{
        saveScenePlanPoint: {
          planPoint: { id: string } | null;
          errors: string[];
        };
      }>(SAVE_SCENE_PLAN_POINT, {
        sceneId: cover.id,
        planPointId: km.id ?? undefined,
        content: text,
        position: kmIdx,
      });
      const newId = res.saveScenePlanPoint?.planPoint?.id;
      if (newId && !km.id) {
        setPlanPointIdAt(chapterIdx, coverIdx, kmIdx, newId);
      }
    } catch (err) {
      captureException(err);
    }
  };

  const proposeScenePlanFor = async (sceneId: string) => {
    setProposingForSceneId(sceneId);
    try {
      const client = getGraphQLClient();
      const res = await client.request<{
        generateScenePlanPointSuggestions: {
          errors: string[];
          suggestions: { items: { content: string }[] } | null;
        };
      }>(GENERATE_SCENE_PLAN_POINT_SUGGESTIONS, { sceneId, mode: 'fresh' });
      const items = res.generateScenePlanPointSuggestions?.suggestions?.items ?? [];
      const contents = items.map((it) => it.content).filter((c) => c && c.trim().length > 0);
      setProposalsBySceneId((prev) => ({ ...prev, [sceneId]: contents }));
      // Auto-expand the scene so the user sees them.
      setExpandedSceneIds((prev) => {
        const next = new Set(prev);
        next.add(sceneId);
        return next;
      });
    } catch (err) {
      captureException(err);
    } finally {
      setProposingForSceneId(null);
    }
  };

  const acceptProposal = async (chapterIdx: number, coverIdx: number, content: string) => {
    const cover = chapters?.[chapterIdx]?.covers[coverIdx];
    if (!cover || !cover.id) return;
    // Append to local state, dismiss the ghost, then persist.
    const nextIdx = cover.scenePlan.length;
    updateScenePlanAt(chapterIdx, coverIdx, [...cover.scenePlan, { id: null, text: content }]);
    dismissProposal(cover.id, content);
    try {
      const client = getGraphQLClient();
      const res = await client.request<{
        saveScenePlanPoint: {
          planPoint: { id: string } | null;
          errors: string[];
        };
      }>(SAVE_SCENE_PLAN_POINT, {
        sceneId: cover.id,
        content,
        position: nextIdx,
      });
      const newId = res.saveScenePlanPoint?.planPoint?.id;
      if (newId) setPlanPointIdAt(chapterIdx, coverIdx, nextIdx, newId);
    } catch (err) {
      captureException(err);
    }
  };

  const dismissProposal = (sceneId: string, content: string) => {
    setDismissedProposals((prev) => {
      const next = new Set(prev);
      next.add(`${sceneId}::${content}`);
      return next;
    });
  };

  const persistPlanPointPositions = (sceneId: string | null, kms: PlanPoint[]) => {
    if (!sceneId || structureLocked) return;
    const client = getGraphQLClient();
    kms.forEach((km, i) => {
      if (!km.id) return;
      client
        .request(SAVE_SCENE_PLAN_POINT, {
          sceneId,
          planPointId: km.id,
          content: km.text,
          position: i,
        })
        .catch(captureException);
    });
  };

  const movePlanPointAt = (chapterIdx: number, coverIdx: number, kmIdx: number, dir: -1 | 1) => {
    setChapters((prev) => {
      if (!prev) return prev;
      const chapter = prev[chapterIdx];
      if (!chapter) return prev;
      const cover = chapter.covers[coverIdx];
      if (!cover) return prev;
      const target = kmIdx + dir;
      if (target < 0 || target >= cover.scenePlan.length) return prev;
      const nextKms = [...cover.scenePlan];
      [nextKms[kmIdx], nextKms[target]] = [nextKms[target], nextKms[kmIdx]];
      persistPlanPointPositions(cover.id, nextKms);
      const nextCovers = chapter.covers.map((c, i) =>
        i === coverIdx ? { ...c, scenePlan: nextKms } : c
      );
      return prev.map((c, i) => (i === chapterIdx ? { ...chapter, covers: nextCovers } : c));
    });
  };

  const removeChapter = (idx: number) => {
    const chapter = chapters?.[idx];
    setChapters((prev) => (prev ? prev.filter((_, i) => i !== idx) : prev));
    setEditingTitleIndex(null);
    if (chapter?.chapterId && !structureLocked) {
      const client = getGraphQLClient();
      client.request(DELETE_CHAPTER, { chapterId: chapter.chapterId }).catch(captureException);
    }
  };

  const persistChapterOrder = (next: HydratedChapter[]) => {
    if (!bookId || structureLocked) return;
    const ids = next.map((c) => c.chapterId).filter((id): id is string => !!id);
    if (ids.length !== next.length) return;
    const client = getGraphQLClient();
    client.request(REORDER_CHAPTERS, { bookId, chapterIds: ids }).catch(captureException);
  };

  const moveChapter = (idx: number, direction: -1 | 1) => {
    setChapters((prev) => {
      if (!prev) return prev;
      const target = idx + direction;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[target]] = [next[target], next[idx]];
      persistChapterOrder(next);
      return next;
    });
  };

  const insertChapter = (atIdx: number) => {
    setChapters((prev) => {
      const base = prev ?? [];
      const next = [...base];
      next.splice(atIdx, 0, emptyChapter());
      return next;
    });
    setEditingTitleIndex(atIdx);
  };

  const appendChapter = () => {
    setChapters((prev) => {
      const base = prev ?? [];
      const nextIdx = base.length;
      const next = [...base, emptyChapter()];
      // schedule edit after state lands
      setEditingTitleIndex(nextIdx);
      return next;
    });
  };

  // Open the scene-suggestor modal for the given chapter and fetch
  // suggestions. Each suggestion is shown as a row with a "+" the user
  // clicks to add it; nothing is appended until they pick.
  const suggestCovers = async (idx: number) => {
    if (!bookId || !chapters) return;
    const chapter = chapters[idx];
    const title = chapter.title.trim();
    if (!title) {
      setEditingTitleIndex(idx);
      return;
    }
    setSceneSuggestionsModalIdx(idx);
    setSceneSuggestionsLoading(true);
    setSceneSuggestionsError(null);
    setSuggestingForIndex(idx);
    try {
      const client = getGraphQLClient();
      const siblings = chapters
        .filter((_, i) => i !== idx)
        .map((c) => c.title.trim())
        .filter((s) => s.length > 0);
      const data = await client.request<GenerateCoversResponse>(GENERATE_CHAPTER_COVERS, {
        bookId,
        chapterTitle: title,
        instructions: brief.trim() || null,
        siblings,
      });
      const result = data.generateChapterCovers;
      if (!result.success || !result.covers) {
        setSceneSuggestionsError(result.errors.join(', ') || t('common.error'));
        return;
      }
      const cleaned = result.covers.map((text) => text.trim()).filter((text) => text.length > 0);
      setSceneSuggestionsByIdx((prev) => ({ ...prev, [idx]: cleaned }));
    } catch (err) {
      captureException(err);
      setSceneSuggestionsError(t('common.error'));
    } finally {
      setSceneSuggestionsLoading(false);
      setSuggestingForIndex(null);
    }
  };

  const addSuggestedScene = async (chapterIdx: number, content: string) => {
    const chapter = chapters?.[chapterIdx];
    if (!chapter) return;
    // Remove from the suggestion list immediately so the row disappears.
    setSceneSuggestionsByIdx((prev) => {
      const list = prev[chapterIdx] ?? [];
      return { ...prev, [chapterIdx]: list.filter((c) => c !== content) };
    });
    // Optimistically append to local state.
    const insertedAt = chapter.covers.length;
    updateCovers(chapterIdx, [...chapter.covers, { id: null, text: content, scenePlan: [] }]);
    // Post-commit: persist as a Scene.
    if (chapter.chapterId && !structureLocked) {
      try {
        const client = getGraphQLClient();
        const res = await client.request<{
          createScene: { success: boolean; errors: string[]; scene: { id: string } | null };
        }>(CREATE_SCENE, {
          chapterId: chapter.chapterId,
          title: content,
          position: insertedAt,
        });
        const newId = res.createScene?.scene?.id;
        if (newId) setCoverIdAt(chapterIdx, insertedAt, newId);
      } catch (err) {
        captureException(err);
      }
    }
  };

  const dismissSuggestedScene = (chapterIdx: number, content: string) => {
    setSceneSuggestionsByIdx((prev) => {
      const list = prev[chapterIdx] ?? [];
      return { ...prev, [chapterIdx]: list.filter((c) => c !== content) };
    });
  };

  const closeSceneSuggestions = () => {
    setSceneSuggestionsModalIdx(null);
    setSceneSuggestionsError(null);
  };

  const continueAction = () => {
    if (!bookId || !committed || !chapters || chapters.length === 0) return;
    setIsContinuing(true);
    setApplyError(null);
    // Chapters are already committed by Generate — Continue is just navigation.
    navigate(`/books/${bookId}/refine?generate=true`);
  };

  const generateLabel = hasChapters
    ? t('chaptersFirst.regenerateWithAi', 'Regenerate table of contents')
    : t('chaptersFirst.generateWithAi', 'Generate table of contents');

  return (
    <div className="max-w-4xl mx-auto p-6 pb-24 space-y-6">
      <header>
        <div className="flex flex-wrap items-center gap-2 mb-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
          <span>{t('chaptersFirst.stepIndicator', 'Step 1 of 2')}</span>
          <span aria-hidden>·</span>
          <span className="text-primary-600 dark:text-primary-400">
            {t('chaptersFirst.stepStructure', 'Structure')}
          </span>
          <span aria-hidden>→</span>
          <span>{t('chaptersFirst.stepRefine', 'Refine')}</span>
        </div>
        <h1 className="text-2xl font-bold mb-1">
          {t('chaptersFirst.selectStructure', 'Choose your chapter structure')}
        </h1>
        {bookTitle && (
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2 truncate">
            {bookTitle}
          </p>
        )}
        <p className="text-gray-600 dark:text-gray-400">
          {t(
            'chaptersFirst.selectStructureHelp',
            'Pick a length, write a short brief, and we’ll draft a table of contents.'
          )}
        </p>
      </header>

      {committed && (
        <div className="rounded-lg border border-amber-200 dark:border-amber-800/60 bg-amber-50 dark:bg-amber-900/20 px-4 py-3 text-sm text-amber-900 dark:text-amber-100">
          <p className="font-medium">
            {t('chaptersFirst.committedNoticeTitle', 'These chapters are saved.')}
          </p>
          <p className="mt-0.5 text-amber-800/90 dark:text-amber-100/80">
            {t(
              'chaptersFirst.committedNoticeBody',
              'To rework the structure, regenerate — this erases the chapters and any draft work tied to them. To tweak a single chapter, use the workspace.'
            )}
          </p>
        </div>
      )}

      {hasChapters && !settingsOpen && !structureLocked && (
        <SettingsStrip
          targetChapters={targetChapters}
          brief={brief}
          onExpand={() => setSettingsOpen(true)}
          t={t}
        />
      )}

      {!structureLocked && (!hasChapters || settingsOpen) && (
        <Card padding="lg">
          <div className="space-y-5">
            {hasChapters && (
              <div className="flex justify-end -mt-1 -mr-1">
                <button
                  type="button"
                  onClick={() => setSettingsOpen(false)}
                  className="text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:underline"
                >
                  {t('chaptersFirst.collapseSettings', 'Hide settings')}
                </button>
              </div>
            )}
            <div>
              <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2">
                {t('chaptersFirst.chapterCount', 'How long should the book be?')}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {PRESETS.map((preset) => (
                  <PresetCard
                    key={preset.id}
                    preset={preset}
                    selected={selectedPresetId === preset.id}
                    onSelect={() => pickPreset(preset)}
                    t={t}
                  />
                ))}
              </div>
              <div className="mt-3">
                {manualOpen ? (
                  <div>
                    <label
                      htmlFor="targetChapters"
                      className="inline-flex items-center gap-2 rounded-xl border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-1.5 shadow-sm hover:border-primary-400 dark:hover:border-primary-600 focus-within:border-primary-500 dark:focus-within:border-primary-400 focus-within:ring-4 focus-within:ring-primary-500/20 transition-all"
                    >
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {t('chaptersFirst.approxLabel', 'Approximately')}
                      </span>
                      <input
                        id="targetChapters"
                        type="number"
                        min={COUNT_FLOOR}
                        max={COUNT_CEILING}
                        value={targetChapters}
                        onChange={(e) => handleTargetChange(e.target.value)}
                        className="w-12 bg-transparent text-center text-base font-semibold text-gray-900 dark:text-gray-100 focus:outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                      />
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {t('chaptersFirst.chaptersUnit', 'chapters')}
                      </span>
                    </label>
                    <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                      {t(
                        'chaptersFirst.approxHint',
                        "Tweak if you have a number in mind — we'll stay close to it."
                      )}
                    </p>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setManualOpen(true)}
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 hover:underline transition-colors"
                  >
                    <PencilIcon className="w-3.5 h-3.5" />
                    {t('chaptersFirst.setManually', 'Enter your own number')}
                  </button>
                )}
              </div>
            </div>

            <div>
              <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1">
                {t('chaptersFirst.briefLabel', 'What should the table of contents contain?')}
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                {t(
                  'chaptersFirst.briefHelp',
                  'Describe themes, structure, or specific topics you want covered.'
                )}
              </p>
              <AiEnhancedTextarea
                value={brief}
                onChange={(e) => setBrief(e.target.value)}
                placeholder={t(
                  'chaptersFirst.briefPlaceholder',
                  "e.g. Chapters are written from each character's perspective, with Jon's experience taking center stage…"
                )}
                minRows={3}
                bookId={bookId}
                onQuickAction={handleBriefQuickAction}
                hasContent={briefFilled}
                showMenu={false}
                quickActionLabel={t('chaptersFirst.suggestBrief', 'Suggest a brief with AI')}
              />
              {showBriefSuggestions && (
                <ChapterFlowBriefPicker
                  suggestions={briefSuggestions}
                  isLoading={briefSuggestionsLoading}
                  onSelect={handleBriefSuggestionSelect}
                  onRefresh={refreshBriefSuggestions}
                  onClose={() => setShowBriefSuggestions(false)}
                  t={t}
                />
              )}
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2 border-t border-gray-100 dark:border-gray-700">
              <p className="text-xs text-gray-500 dark:text-gray-400 flex-1">
                {!briefFilled
                  ? t(
                      'chaptersFirst.generateDisabledHint',
                      'Fill in “What should the table of contents contain?” above to enable AI generation.'
                    )
                  : hasChapters
                    ? t(
                        'chaptersFirst.regenerateHint',
                        'Edit the brief and regenerate, or regenerate with the same brief.'
                      )
                    : t(
                        'chaptersFirst.generateHint',
                        'We’ll draft a single table of contents based on your brief and chapter range.'
                      )}
              </p>
              <Button
                onClick={generate}
                isLoading={isGenerating}
                disabled={!briefFilled || isGenerating}
              >
                {hasChapters
                  ? t('chaptersFirst.regenerateSameBrief', 'Regenerate table of contents')
                  : generateLabel}
              </Button>
            </div>
            {generateError && <p className="mt-1 text-sm text-red-600">{generateError}</p>}
          </div>
        </Card>
      )}

      {isGenerating && !hasChapters && (
        <div className="flex items-center justify-center gap-3 py-8 text-gray-600 dark:text-gray-400">
          <Spinner />
          <span>{t('chaptersFirst.generatingToc', 'Drafting your table of contents…')}</span>
        </div>
      )}

      {options && !hasChapters && !isGenerating && (
        <OptionsPicker
          options={options}
          expandedOptionId={expandedOptionId}
          applyingOptionId={applyingOptionId}
          onToggleExpand={(id) => setExpandedOptionId((prev) => (prev === id ? null : id))}
          onPick={(option) => void applyOption(option)}
          applyError={applyError}
          t={t}
        />
      )}

      {hasChapters && (
        <Card padding="lg">
          <div className="flex items-baseline justify-between mb-4 gap-3">
            <h2 className="text-lg font-semibold">
              {t('chaptersFirst.tocTitle', 'Table of contents')}
            </h2>
            {!committed && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t('chaptersFirst.chapterTitleEditHint', 'Click any title to edit')}
              </p>
            )}
          </div>
          <ChapterList
            chapters={chapters!}
            editingTitleIndex={editingTitleIndex}
            suggestingForIndex={suggestingForIndex}
            readOnly={structureLocked}
            allowStructureChanges={!structureLocked}
            onStartEditingTitle={(idx) => setEditingTitleIndex(idx)}
            onStopEditingTitle={(idx) => {
              setEditingTitleIndex(null);
              if (idx !== null && idx !== undefined) void persistChapterTitle(idx);
            }}
            onTitleChange={updateChapterTitle}
            onCoverChange={updateCoverAt}
            onCoverRemove={(chapterIdx, coverIdx) => removeCoverAt(chapterIdx, coverIdx)}
            onCoverAdd={addCoverAt}
            onCoverBlur={(chapterIdx, coverIdx) => void persistCoverAt(chapterIdx, coverIdx)}
            onCoverMove={moveCoverAt}
            onRemove={removeChapter}
            onMove={moveChapter}
            onSuggestCovers={suggestCovers}
            onInsertAt={insertChapter}
            planPointActions={{
              expandedSceneIds,
              proposalsBySceneId,
              proposingForSceneId,
              dismissedProposals,
              onToggleSceneExpanded: toggleSceneExpanded,
              onChange: updatePlanPointAt,
              onAdd: addPlanPointAt,
              onRemove: removePlanPointAt,
              onBlur: (ci, ki, kmi) => void persistPlanPointAt(ci, ki, kmi),
              onMove: movePlanPointAt,
              onPropose: (sceneId) => void proposeScenePlanFor(sceneId),
              onAcceptProposal: acceptProposal,
              onDismissProposal: dismissProposal,
            }}
            t={t}
          />
          {!structureLocked && (
            <div className="mt-3">
              <button
                type="button"
                onClick={appendChapter}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-700 text-sm font-medium text-gray-600 dark:text-gray-300 hover:border-primary-400 dark:hover:border-primary-600 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
              >
                <PlusIcon className="w-4 h-4" />
                {t('chaptersFirst.addChapter', 'Add chapter')}
              </button>
            </div>
          )}
        </Card>
      )}

      <ConfirmDialog
        isOpen={confirmRegenerateOpen}
        onClose={() => setConfirmRegenerateOpen(false)}
        onConfirm={confirmRegenerate}
        isProcessing={isGenerating}
        confirmVariant="danger"
        title={t('chaptersFirst.confirmRegenerateTitle', 'Erase chapters and regenerate?')}
        message={t(
          'chaptersFirst.confirmRegenerateBody',
          'This deletes the current chapters along with their scenes, plot beats and any draft text tied to them. This cannot be undone.'
        )}
        confirmText={t('chaptersFirst.confirmRegenerateAction', 'Erase and regenerate')}
        cancelText={t('common.cancel', 'Cancel')}
      />

      <SceneSuggestionsModal
        isOpen={sceneSuggestionsModalIdx !== null}
        isLoading={sceneSuggestionsLoading}
        suggestions={
          sceneSuggestionsModalIdx !== null
            ? (sceneSuggestionsByIdx[sceneSuggestionsModalIdx] ?? [])
            : []
        }
        chapterTitle={
          sceneSuggestionsModalIdx !== null
            ? (chapters?.[sceneSuggestionsModalIdx]?.title ?? '')
            : ''
        }
        error={sceneSuggestionsError}
        onAdd={(content) => {
          if (sceneSuggestionsModalIdx === null) return;
          void addSuggestedScene(sceneSuggestionsModalIdx, content);
        }}
        onDismiss={(content) => {
          if (sceneSuggestionsModalIdx === null) return;
          dismissSuggestedScene(sceneSuggestionsModalIdx, content);
        }}
        onClose={closeSceneSuggestions}
        t={t}
      />

      {applyError && <p className="text-red-600">{applyError}</p>}

      <div className="sticky bottom-0 -mx-6 px-6 py-4 mt-8 bg-white/95 dark:bg-gray-900/95 backdrop-blur border-t border-gray-200 dark:border-gray-700 flex items-center justify-between gap-4">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {!hasChapters
            ? t(
                'chaptersFirst.continueHintPreGenerate',
                'Generate a table of contents above to continue.'
              )
            : chapters!.length === 0
              ? t('chaptersFirst.continueHintEmpty', 'Add at least one chapter to continue.')
              : t(
                  'chaptersFirst.continueHintReady',
                  'Ready to draft your book from this structure.'
                )}
        </p>
        <Button
          onClick={continueAction}
          isLoading={isContinuing}
          disabled={!committed || !hasChapters || (chapters?.length ?? 0) === 0}
          rightIcon={
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M14 5l7 7m0 0l-7 7m7-7H3"
              />
            </svg>
          }
        >
          {t('chaptersFirst.continue', 'Continue')}
        </Button>
      </div>
    </div>
  );
}

type SettingsStripProps = {
  targetChapters: number;
  brief: string;
  onExpand: () => void;
  t: TFunction;
};

function SettingsStrip({ targetChapters, brief, onExpand, t }: SettingsStripProps) {
  const trimmed = brief.trim();
  const preview = trimmed.length > 100 ? `${trimmed.slice(0, 100).trimEnd()}…` : trimmed;
  return (
    <button
      type="button"
      onClick={onExpand}
      className="w-full text-left flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 hover:border-primary-400 dark:hover:border-primary-600 transition-colors"
    >
      <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300">
        <SlidersIcon className="w-3.5 h-3.5" />
        {t('chaptersFirst.settingsStripLabel', 'Settings')}
      </span>
      <span className="text-sm text-gray-700 dark:text-gray-200">
        ~{targetChapters} {t('chaptersFirst.chaptersUnit', 'chapters')}
      </span>
      {trimmed && (
        <>
          <span className="text-gray-300 dark:text-gray-600" aria-hidden>
            ·
          </span>
          <span className="text-sm text-gray-600 dark:text-gray-300 truncate flex-1 min-w-0">
            “{preview}”
          </span>
        </>
      )}
      <span className="ml-auto inline-flex items-center gap-1 text-sm font-medium text-primary-600 dark:text-primary-400">
        {t('chaptersFirst.editAndRegenerate', 'Edit & regenerate')}
        <svg
          className="w-3.5 h-3.5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </span>
    </button>
  );
}

type ChapterFlowBriefPickerProps = {
  suggestions: string[];
  isLoading: boolean;
  onSelect: (raw: string) => void;
  onRefresh: () => void;
  onClose: () => void;
  t: TFunction;
};

function ChapterFlowBriefPicker({
  suggestions,
  isLoading,
  onSelect,
  onRefresh,
  onClose,
  t,
}: ChapterFlowBriefPickerProps) {
  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      className="mt-3 p-4 bg-gradient-to-br from-primary-50 to-primary-100/50 dark:from-primary-900/20 dark:to-primary-800/10 rounded-xl border border-primary-200 dark:border-primary-800"
    >
      <div className="flex items-start justify-between mb-1 gap-3">
        <div>
          <p className="text-sm font-medium text-primary-700 dark:text-primary-300">
            {t('chaptersFirst.briefSuggestionsTitleThree', 'Three angles to choose from')}
          </p>
          <p className="text-xs text-primary-600/80 dark:text-primary-300/70 mt-0.5">
            {t(
              'chaptersFirst.briefSuggestionsHintThree',
              'Same book, different angle each. Pick one, then tweak.'
            )}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('common.close', 'Close')}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors shrink-0"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      {isLoading && suggestions.length === 0 ? (
        <div className="flex items-center justify-center py-8">
          <Spinner size="md" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-2 mt-3">
            {suggestions.map((raw, index) => {
              const { handle, body } = parseSuggestion(raw);
              return (
                <motion.button
                  key={`${raw}-${index}`}
                  type="button"
                  onClick={() => onSelect(raw)}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.08 }}
                  className="text-left p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-primary-400 dark:hover:border-primary-500 hover:shadow-md transition-all duration-200 group flex flex-col gap-2"
                >
                  {handle && (
                    <span className="inline-flex self-start items-center px-2 py-0.5 rounded-full bg-primary-100 dark:bg-primary-900/40 text-primary-800 dark:text-primary-200 text-xs font-semibold uppercase tracking-wide">
                      {handle}
                    </span>
                  )}
                  <span className="text-sm text-gray-700 dark:text-gray-200 group-hover:text-gray-900 dark:group-hover:text-gray-100 transition-colors whitespace-pre-wrap">
                    {body}
                  </span>
                </motion.button>
              );
            })}
          </div>

          <button
            type="button"
            onClick={onRefresh}
            disabled={isLoading}
            className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 bg-white dark:bg-gray-800 border border-primary-300 dark:border-primary-700 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            )}
            {t('chaptersFirst.rerollSuggestions', 'Try different ones')}
          </button>
        </>
      )}
    </motion.div>
  );
}

type PresetCardProps = {
  preset: Preset;
  selected: boolean;
  onSelect: () => void;
  t: TFunction;
};

function PresetCard({ preset, selected, onSelect, t }: PresetCardProps) {
  const labelKey = `chaptersFirst.preset.${preset.id}.label`;
  const descKey = `chaptersFirst.preset.${preset.id}.description`;
  const fallbackLabels: Record<Preset['id'], string> = {
    short: 'Quick read',
    standard: 'Standard',
    long: 'In-depth',
  };
  const fallbackDescriptions: Record<Preset['id'], string> = {
    short: 'A focused, single-sitting read.',
    standard: 'A typical book length for most topics.',
    long: 'A thorough, deep treatment.',
  };
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`text-left rounded-lg border p-3 transition ${
        selected
          ? 'border-indigo-500 ring-2 ring-indigo-200 bg-indigo-50 dark:bg-indigo-900/20'
          : 'border-gray-200 dark:border-gray-700 hover:border-gray-400'
      }`}
    >
      <div className="flex items-baseline justify-between gap-2">
        <span className="font-semibold text-gray-900 dark:text-gray-100">
          {t(labelKey, fallbackLabels[preset.id])}
        </span>
        <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
          {preset.min}–{preset.max} {t('chaptersFirst.chaptersUnit', 'chapters')}
        </span>
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
        {t(descKey, fallbackDescriptions[preset.id])}
      </p>
    </button>
  );
}

type PlanPointActions = {
  expandedSceneIds: Set<string>;
  proposalsBySceneId: Record<string, string[]>;
  proposingForSceneId: string | null;
  dismissedProposals: Set<string>;
  onToggleSceneExpanded: (sceneId: string) => void;
  onChange: (chapterIdx: number, coverIdx: number, kmIdx: number, value: string) => void;
  onAdd: (chapterIdx: number, coverIdx: number) => void;
  onRemove: (chapterIdx: number, coverIdx: number, kmIdx: number) => void;
  onBlur: (chapterIdx: number, coverIdx: number, kmIdx: number) => void;
  onMove: (chapterIdx: number, coverIdx: number, kmIdx: number, dir: -1 | 1) => void;
  onPropose: (sceneId: string) => void;
  onAcceptProposal: (chapterIdx: number, coverIdx: number, content: string) => void;
  onDismissProposal: (sceneId: string, content: string) => void;
};

type ChapterListProps = {
  chapters: HydratedChapter[];
  editingTitleIndex: number | null;
  suggestingForIndex: number | null;
  readOnly: boolean;
  allowStructureChanges: boolean;
  onStartEditingTitle: (idx: number) => void;
  onStopEditingTitle: (idx: number | null) => void;
  onTitleChange: (index: number, value: string) => void;
  onCoverChange: (chapterIdx: number, coverIdx: number, value: string) => void;
  onCoverRemove: (chapterIdx: number, coverIdx: number) => void;
  onCoverAdd: (chapterIdx: number) => void;
  onCoverBlur: (chapterIdx: number, coverIdx: number) => void;
  onCoverMove: (chapterIdx: number, coverIdx: number, dir: -1 | 1) => void;
  onRemove: (index: number) => void;
  onMove: (index: number, dir: -1 | 1) => void;
  onSuggestCovers: (index: number) => void;
  onInsertAt: (index: number) => void;
  planPointActions: PlanPointActions;
  t: TFunction;
};

function ChapterList({
  chapters,
  editingTitleIndex,
  suggestingForIndex,
  readOnly,
  allowStructureChanges,
  onStartEditingTitle,
  onStopEditingTitle,
  onTitleChange,
  onCoverChange,
  onCoverRemove,
  onCoverAdd,
  onCoverBlur,
  onCoverMove,
  onRemove,
  onMove,
  onSuggestCovers,
  onInsertAt,
  planPointActions,
  t,
}: ChapterListProps) {
  return (
    <ol className="space-y-2">
      {chapters.map((chapter, idx) => (
        <div key={chapter.chapterId ?? `local-${idx}`}>
          {allowStructureChanges && idx > 0 && (
            <InsertBetween
              onInsert={() => onInsertAt(idx)}
              label={t('chaptersFirst.insertChapter', 'Insert chapter here')}
            />
          )}
          <ChapterRow
            chapter={chapter}
            index={idx}
            isFirst={idx === 0}
            isLast={idx === chapters.length - 1}
            isEditingTitle={editingTitleIndex === idx}
            isSuggesting={suggestingForIndex === idx}
            readOnly={readOnly}
            allowStructureChanges={allowStructureChanges}
            onStartEditingTitle={() => onStartEditingTitle(idx)}
            onStopEditingTitle={() => onStopEditingTitle(idx)}
            onTitleChange={(value) => onTitleChange(idx, value)}
            onCoverChange={(coverIdx, value) => onCoverChange(idx, coverIdx, value)}
            onCoverRemove={(coverIdx) => onCoverRemove(idx, coverIdx)}
            onCoverAdd={() => onCoverAdd(idx)}
            onCoverBlur={(coverIdx) => onCoverBlur(idx, coverIdx)}
            onCoverMove={(coverIdx, dir) => onCoverMove(idx, coverIdx, dir)}
            onRemove={() => onRemove(idx)}
            onMoveUp={() => onMove(idx, -1)}
            onMoveDown={() => onMove(idx, 1)}
            onSuggestCovers={() => onSuggestCovers(idx)}
            chapterIdx={idx}
            planPointActions={planPointActions}
            t={t}
          />
        </div>
      ))}
    </ol>
  );
}

function InsertBetween({ onInsert, label }: { onInsert: () => void; label: string }) {
  return (
    <div className="group relative h-2 -my-1">
      <button
        type="button"
        onClick={onInsert}
        aria-label={label}
        className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
      >
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 text-xs font-medium">
          <PlusIcon className="w-3 h-3" />
          {label}
        </span>
      </button>
    </div>
  );
}

type ChapterRowProps = {
  chapter: HydratedChapter;
  index: number;
  isFirst: boolean;
  isLast: boolean;
  isEditingTitle: boolean;
  isSuggesting: boolean;
  readOnly: boolean;
  allowStructureChanges: boolean;
  onStartEditingTitle: () => void;
  onStopEditingTitle: () => void;
  onTitleChange: (value: string) => void;
  onCoverChange: (coverIdx: number, value: string) => void;
  onCoverRemove: (coverIdx: number) => void;
  onCoverAdd: () => void;
  onCoverBlur: (coverIdx: number) => void;
  onCoverMove: (coverIdx: number, dir: -1 | 1) => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onSuggestCovers: () => void;
  chapterIdx: number;
  planPointActions: PlanPointActions;
  t: TFunction;
};

function ChapterRow({
  chapter,
  index,
  isFirst,
  isLast,
  isEditingTitle,
  isSuggesting,
  readOnly,
  allowStructureChanges,
  onStartEditingTitle,
  onStopEditingTitle,
  onTitleChange,
  onCoverChange,
  onCoverRemove,
  onCoverAdd,
  onCoverBlur,
  onCoverMove,
  onRemove,
  onMoveUp,
  onMoveDown,
  onSuggestCovers,
  chapterIdx,
  planPointActions,
  t,
}: ChapterRowProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = () => setMenuOpen(false);

  return (
    <li className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-white dark:bg-gray-800">
      <div className="flex items-start gap-3">
        <span className="text-gray-400 font-medium pt-1.5 w-6 text-right shrink-0">
          {index + 1}.
        </span>
        <div className="flex-1 min-w-0">
          {isEditingTitle && !readOnly ? (
            <Input
              value={chapter.title}
              onChange={(e) => onTitleChange(e.target.value)}
              onBlur={onStopEditingTitle}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === 'Escape') {
                  e.preventDefault();
                  onStopEditingTitle();
                }
              }}
              placeholder={t('chaptersFirst.chapterTitle', 'Chapter title')}
              autoFocus
            />
          ) : readOnly ? (
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">
              {chapter.title || (
                <span className="text-gray-400 italic">
                  {t('chaptersFirst.untitledChapter', 'Untitled chapter')}
                </span>
              )}
            </h3>
          ) : (
            <button
              type="button"
              onClick={onStartEditingTitle}
              className="group block w-full text-left rounded-md -mx-2 px-2 py-1 hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors cursor-text"
            >
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 group-hover:text-primary-700 dark:group-hover:text-primary-300 transition-colors">
                {chapter.title || (
                  <span className="text-gray-400 italic">
                    {t('chaptersFirst.untitledChapter', 'Untitled chapter')}
                  </span>
                )}
              </h3>
            </button>
          )}

          <div className="mt-3">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5">
              {t('chaptersFirst.chapterCoversCaption', 'Scenes in this chapter')}
            </p>
            {readOnly ? (
              chapter.covers.length === 0 ? (
                <p className="text-xs italic text-gray-400 dark:text-gray-500">
                  {t('chaptersFirst.noPointsCommitted', 'No scenes stored for this chapter.')}
                </p>
              ) : (
                <ul className="space-y-1 list-disc pl-5">
                  {chapter.covers.map((cover, ci) => (
                    <li
                      key={cover.id ?? ci}
                      className="text-sm text-gray-700 dark:text-gray-200 leading-relaxed"
                    >
                      {cover.text}
                    </li>
                  ))}
                </ul>
              )
            ) : (
              <>
                <CoversEditor
                  covers={chapter.covers}
                  chapterIdx={chapterIdx}
                  onChange={onCoverChange}
                  onRemove={onCoverRemove}
                  onAdd={onCoverAdd}
                  onBlur={onCoverBlur}
                  onMove={onCoverMove}
                  planPointActions={planPointActions}
                  t={t}
                />
                <div className="mt-2 flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={onCoverAdd}
                    className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                  >
                    <PlusIcon className="w-3 h-3" />
                    {t('chaptersFirst.addPoint', 'Add a scene')}
                  </button>
                  <button
                    type="button"
                    onClick={onSuggestCovers}
                    disabled={isSuggesting}
                    className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isSuggesting ? <Spinner size="sm" /> : <SparkleIcon className="w-3.5 h-3.5" />}
                    {chapter.covers.length === 0
                      ? t('chaptersFirst.suggestPoints', 'Suggest scenes with AI')
                      : t('chaptersFirst.suggestMorePoints', 'Suggest more scenes with AI')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
        {allowStructureChanges && !readOnly && (
          <div className="relative shrink-0">
            <button
              type="button"
              onClick={() => setMenuOpen((open) => !open)}
              aria-label={t('common.options', 'Options')}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              className="inline-flex items-center justify-center w-8 h-8 rounded-md text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
                <circle cx="10" cy="4" r="1.6" />
                <circle cx="10" cy="10" r="1.6" />
                <circle cx="10" cy="16" r="1.6" />
              </svg>
            </button>
            <DropdownMenu isOpen={menuOpen} onClose={closeMenu} position="right" width="md">
              <DropdownMenuItem
                onClick={() => {
                  onStartEditingTitle();
                  closeMenu();
                }}
              >
                {t('common.edit', 'Edit')}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  onMoveUp();
                  closeMenu();
                }}
                disabled={isFirst}
              >
                {t('common.moveUp', 'Move up')}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  onMoveDown();
                  closeMenu();
                }}
                disabled={isLast}
              >
                {t('common.moveDown', 'Move down')}
              </DropdownMenuItem>
              <DropdownMenuItem
                variant="danger"
                onClick={() => {
                  onRemove();
                  closeMenu();
                }}
              >
                {t('common.remove', 'Remove')}
              </DropdownMenuItem>
            </DropdownMenu>
          </div>
        )}
      </div>
    </li>
  );
}

type CoversEditorProps = {
  covers: Cover[];
  chapterIdx: number;
  onChange: (coverIdx: number, value: string) => void;
  onRemove: (coverIdx: number) => void;
  onAdd: () => void;
  onBlur: (coverIdx: number) => void;
  onMove: (coverIdx: number, dir: -1 | 1) => void;
  planPointActions: PlanPointActions;
  t: TFunction;
};

function CoversEditor({
  covers,
  chapterIdx,
  onChange,
  onRemove,
  onAdd,
  onBlur,
  onMove,
  planPointActions,
  t,
}: CoversEditorProps) {
  const focusNextRef = useRef<number | null>(null);
  const inputRefs = useRef<Array<HTMLTextAreaElement | null>>([]);

  useEffect(() => {
    if (focusNextRef.current !== null) {
      const el = inputRefs.current[focusNextRef.current];
      if (el) {
        el.focus();
        const len = el.value.length;
        try {
          el.setSelectionRange(len, len);
        } catch {
          /* noop */
        }
      }
      focusNextRef.current = null;
    }
  });

  if (covers.length === 0) {
    return (
      <p className="text-xs italic text-gray-400 dark:text-gray-500">
        {t(
          'chaptersFirst.noPointsYet',
          'No scenes yet — add one, or let AI suggest a starting set.'
        )}
      </p>
    );
  }

  return (
    <ul className="space-y-1.5">
      {covers.map((cover, ci) => (
        <SceneRow
          key={cover.id ?? `local-${ci}`}
          cover={cover}
          chapterIdx={chapterIdx}
          coverIdx={ci}
          isFirst={ci === 0}
          isLast={ci === covers.length - 1}
          placeholder={t(
            'chaptersFirst.pointPlaceholder',
            'One sentence — what happens or is learned in this scene…'
          )}
          onChange={(value) => onChange(ci, value)}
          onEnter={() => {
            focusNextRef.current = ci + 1;
            onAdd();
          }}
          onBackspaceEmpty={() => {
            focusNextRef.current = Math.max(0, ci - 1);
            onRemove(ci);
          }}
          onBlur={() => onBlur(ci)}
          onRemove={() => onRemove(ci)}
          onMoveUp={() => onMove(ci, -1)}
          onMoveDown={() => onMove(ci, 1)}
          registerRef={(el) => {
            inputRefs.current[ci] = el;
          }}
          planPointActions={planPointActions}
          t={t}
        />
      ))}
    </ul>
  );
}

type SceneRowProps = {
  cover: Cover;
  chapterIdx: number;
  coverIdx: number;
  isFirst: boolean;
  isLast: boolean;
  placeholder: string;
  onChange: (value: string) => void;
  onEnter: () => void;
  onBackspaceEmpty: () => void;
  onBlur: () => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  registerRef: (el: HTMLTextAreaElement | null) => void;
  planPointActions: PlanPointActions;
  t: TFunction;
};

function SceneRow({
  cover,
  chapterIdx,
  coverIdx,
  isFirst,
  isLast,
  placeholder,
  onChange,
  onEnter,
  onBackspaceEmpty,
  onBlur,
  onRemove,
  onMoveUp,
  onMoveDown,
  registerRef,
  planPointActions,
  t,
}: SceneRowProps) {
  const hasId = !!cover.id;
  const sceneId = cover.id ?? '';
  const isExpanded = hasId && planPointActions.expandedSceneIds.has(sceneId);
  const kmCount = cover.scenePlan.length;
  const proposals = hasId ? (planPointActions.proposalsBySceneId[sceneId] ?? []) : [];
  const visibleProposals = proposals.filter(
    (c) => !planPointActions.dismissedProposals.has(`${sceneId}::${c}`)
  );
  const isProposing = planPointActions.proposingForSceneId === sceneId;

  return (
    <li className="group">
      <div className="flex items-start gap-2">
        <button
          type="button"
          onClick={() => hasId && planPointActions.onToggleSceneExpanded(sceneId)}
          disabled={!hasId}
          aria-label={
            isExpanded
              ? t('chaptersFirst.collapseScene', 'Collapse scene')
              : t('chaptersFirst.expandScene', 'Expand scene')
          }
          className={`shrink-0 mt-1 inline-flex items-center justify-center w-5 h-5 rounded text-gray-400 dark:text-gray-500 ${
            hasId
              ? 'hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-700 dark:hover:text-gray-200 cursor-pointer'
              : 'opacity-40 cursor-default'
          } transition-colors`}
        >
          {hasId ? (
            <ChevronIcon
              className={`w-3.5 h-3.5 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
            />
          ) : (
            <span className="text-sm leading-none">•</span>
          )}
        </button>
        <CoverRow
          value={cover.text}
          placeholder={placeholder}
          onChange={onChange}
          onEnter={onEnter}
          onBackspaceEmpty={onBackspaceEmpty}
          onBlur={onBlur}
          registerRef={registerRef}
        />
        <ProgressDot count={kmCount} t={t} />
        <RowActions
          onMoveUp={onMoveUp}
          onMoveDown={onMoveDown}
          onRemove={onRemove}
          isFirst={isFirst}
          isLast={isLast}
          upLabel={t('chaptersFirst.moveSceneUp', 'Move scene up')}
          downLabel={t('chaptersFirst.moveSceneDown', 'Move scene down')}
          removeLabel={t('chaptersFirst.removePoint', 'Remove scene')}
        />
      </div>

      {isExpanded && hasId && (
        <ScenePlanBlock
          sceneId={sceneId}
          chapterIdx={chapterIdx}
          coverIdx={coverIdx}
          scenePlan={cover.scenePlan}
          proposals={visibleProposals}
          isProposing={isProposing}
          actions={planPointActions}
          t={t}
        />
      )}
    </li>
  );
}

type ProgressDotProps = { count: number; t: TFunction };

function ProgressDot({ count, t }: ProgressDotProps) {
  if (count === 0) return null;
  return (
    <span
      title={t('chaptersFirst.planPointCount', '{{count}} key moments', { count })}
      className="shrink-0 mt-1.5 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-primary-100 dark:bg-primary-900/40 text-[10px] font-semibold text-primary-700 dark:text-primary-300"
    >
      {count}
    </span>
  );
}

type ScenePlanBlockProps = {
  sceneId: string;
  chapterIdx: number;
  coverIdx: number;
  scenePlan: PlanPoint[];
  proposals: string[];
  isProposing: boolean;
  actions: PlanPointActions;
  t: TFunction;
};

function ScenePlanBlock({
  sceneId,
  chapterIdx,
  coverIdx,
  scenePlan,
  proposals,
  isProposing,
  actions,
  t,
}: ScenePlanBlockProps) {
  const focusNextRef = useRef<number | null>(null);
  const inputRefs = useRef<Array<HTMLTextAreaElement | null>>([]);

  useEffect(() => {
    if (focusNextRef.current !== null) {
      const el = inputRefs.current[focusNextRef.current];
      if (el) {
        el.focus();
        const len = el.value.length;
        try {
          el.setSelectionRange(len, len);
        } catch {
          /* noop */
        }
      }
      focusNextRef.current = null;
    }
  });

  return (
    <div className="mt-2 ml-7 pl-3 border-l-2 border-gray-100 dark:border-gray-700 space-y-2">
      <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
        {t('chaptersFirst.scenePlanLabel', 'Key moments in this scene')}
      </p>

      {scenePlan.length === 0 && proposals.length === 0 ? (
        <p className="text-xs italic text-gray-400 dark:text-gray-500">
          {t(
            'chaptersFirst.noScenePlanYet',
            'No key moments yet — add one, or let AI suggest a starting set.'
          )}
        </p>
      ) : (
        <ul className="space-y-1.5">
          {scenePlan.map((km, ki) => (
            <li key={km.id ?? `local-km-${ki}`} className="group flex items-start gap-2">
              <span className="text-gray-300 dark:text-gray-600 select-none pt-1.5 text-xs">◦</span>
              <CoverRow
                value={km.text}
                placeholder={t(
                  'chaptersFirst.planPointPlaceholder',
                  'A single beat — what happens, what is said, or what shifts…'
                )}
                onChange={(value) => actions.onChange(chapterIdx, coverIdx, ki, value)}
                onEnter={() => {
                  focusNextRef.current = ki + 1;
                  actions.onAdd(chapterIdx, coverIdx);
                }}
                onBackspaceEmpty={() => {
                  focusNextRef.current = Math.max(0, ki - 1);
                  actions.onRemove(chapterIdx, coverIdx, ki);
                }}
                onBlur={() => actions.onBlur(chapterIdx, coverIdx, ki)}
                registerRef={(el) => {
                  inputRefs.current[ki] = el;
                }}
              />
              <RowActions
                onMoveUp={() => actions.onMove(chapterIdx, coverIdx, ki, -1)}
                onMoveDown={() => actions.onMove(chapterIdx, coverIdx, ki, 1)}
                onRemove={() => actions.onRemove(chapterIdx, coverIdx, ki)}
                isFirst={ki === 0}
                isLast={ki === scenePlan.length - 1}
                upLabel={t('chaptersFirst.movePlanPointUp', 'Move key moment up')}
                downLabel={t('chaptersFirst.movePlanPointDown', 'Move key moment down')}
                removeLabel={t('chaptersFirst.removePlanPoint', 'Remove key moment')}
              />
            </li>
          ))}

          {proposals.map((content) => (
            <li
              key={`proposal-${content}`}
              className="flex items-start gap-2 rounded-md bg-primary-50/60 dark:bg-primary-900/15 px-2 py-1.5 border border-dashed border-primary-200 dark:border-primary-800/60"
            >
              <SparkleIcon className="w-3 h-3 text-primary-500 dark:text-primary-400 shrink-0 mt-1" />
              <span className="flex-1 text-sm text-gray-700 dark:text-gray-200 italic">
                {content}
              </span>
              <button
                type="button"
                onClick={() => actions.onAcceptProposal(chapterIdx, coverIdx, content)}
                className="shrink-0 text-xs font-medium text-primary-700 dark:text-primary-300 hover:underline"
              >
                {t('chaptersFirst.acceptProposal', 'Add')}
              </button>
              <button
                type="button"
                onClick={() => actions.onDismissProposal(sceneId, content)}
                className="shrink-0 text-xs font-medium text-gray-500 dark:text-gray-400 hover:underline"
              >
                {t('chaptersFirst.dismissProposal', 'Skip')}
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-wrap items-center gap-3 pt-1">
        <button
          type="button"
          onClick={() => actions.onAdd(chapterIdx, coverIdx)}
          className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
        >
          <PlusIcon className="w-3 h-3" />
          {t('chaptersFirst.addPlanPoint', 'Add key moment')}
        </button>
        <button
          type="button"
          onClick={() => actions.onPropose(sceneId)}
          disabled={isProposing}
          className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isProposing ? <Spinner size="sm" /> : <SparkleIcon className="w-3.5 h-3.5" />}
          {scenePlan.length === 0
            ? t('chaptersFirst.suggestScenePlan', 'Suggest key moments with AI')
            : t('chaptersFirst.suggestMoreScenePlan', 'Suggest more')}
        </button>
      </div>
    </div>
  );
}

function ChevronIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
    </svg>
  );
}

type SceneSuggestionsModalProps = {
  isOpen: boolean;
  isLoading: boolean;
  suggestions: string[];
  chapterTitle: string;
  error: string | null;
  onAdd: (content: string) => void;
  onDismiss: (content: string) => void;
  onClose: () => void;
  t: TFunction;
};

function SceneSuggestionsModal({
  isOpen,
  isLoading,
  suggestions,
  chapterTitle,
  error,
  onAdd,
  onDismiss,
  onClose,
  t,
}: SceneSuggestionsModalProps) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('chaptersFirst.suggestScenesModalTitle', 'Suggested scenes')}
      size="lg"
    >
      <div className="space-y-3">
        {chapterTitle && (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            <span className="font-medium text-gray-700 dark:text-gray-300">{chapterTitle}</span>
            {' — '}
            {t(
              'chaptersFirst.suggestScenesModalHint',
              'Pick the ones you want to add to this chapter.'
            )}
          </p>
        )}
        {error && <p className="text-sm text-red-600">{error}</p>}
        {isLoading ? (
          <div className="flex items-center justify-center gap-2 py-8 text-gray-500">
            <Spinner />
            <span>{t('chaptersFirst.generatingToc', 'Drafting…')}</span>
          </div>
        ) : suggestions.length === 0 ? (
          <p className="text-sm italic text-gray-400 dark:text-gray-500 py-4">
            {t(
              'chaptersFirst.suggestScenesEmpty',
              'No suggestions returned — try again or tweak the chapter title first.'
            )}
          </p>
        ) : (
          <ul className="space-y-2">
            {suggestions.map((content) => (
              <li
                key={content}
                className="flex items-start gap-3 rounded-md border border-gray-200 dark:border-gray-700 px-3 py-2 bg-white dark:bg-gray-800"
              >
                <span className="flex-1 text-sm text-gray-800 dark:text-gray-200 leading-relaxed">
                  {content}
                </span>
                <button
                  type="button"
                  onClick={() => onAdd(content)}
                  aria-label={t('chaptersFirst.addSuggestedScene', 'Add')}
                  className="shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-md bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 hover:bg-primary-200 dark:hover:bg-primary-900/60 transition-colors"
                >
                  <PlusIcon className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => onDismiss(content)}
                  aria-label={t('chaptersFirst.dismissProposal', 'Skip')}
                  className="shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-md text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="flex justify-end pt-2">
          <Button onClick={onClose} variant="secondary">
            {t('chaptersFirst.closeSuggestedScenes', 'Done')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

type RowActionsProps = {
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemove: () => void;
  isFirst: boolean;
  isLast: boolean;
  upLabel: string;
  downLabel: string;
  removeLabel: string;
};

function RowActions({
  onMoveUp,
  onMoveDown,
  onRemove,
  isFirst,
  isLast,
  upLabel,
  downLabel,
  removeLabel,
}: RowActionsProps) {
  const btn =
    'shrink-0 inline-flex items-center justify-center w-6 h-6 rounded text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-gray-400 disabled:cursor-not-allowed transition-colors';
  return (
    <div className="opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity flex items-center gap-0.5 mt-0.5">
      <button
        type="button"
        onClick={onMoveUp}
        disabled={isFirst}
        aria-label={upLabel}
        className={btn}
      >
        <ChevronIcon className="w-3.5 h-3.5 -rotate-90" />
      </button>
      <button
        type="button"
        onClick={onMoveDown}
        disabled={isLast}
        aria-label={downLabel}
        className={btn}
      >
        <ChevronIcon className="w-3.5 h-3.5 rotate-90" />
      </button>
      <button
        type="button"
        onClick={onRemove}
        aria-label={removeLabel}
        className="shrink-0 inline-flex items-center justify-center w-6 h-6 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
      >
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>
    </div>
  );
}

type OptionsPickerProps = {
  options: ChapterFlowOption[];
  expandedOptionId: string | null;
  applyingOptionId: string | null;
  onToggleExpand: (optionId: string) => void;
  onPick: (option: ChapterFlowOption) => void;
  applyError: string | null;
  t: TFunction;
};

const PREVIEW_CHAPTERS = 3;

function OptionsPicker({
  options,
  expandedOptionId,
  applyingOptionId,
  onToggleExpand,
  onPick,
  applyError,
  t,
}: OptionsPickerProps) {
  return (
    <div className="space-y-3">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-lg font-semibold">
          {t('chaptersFirst.pickOptionTitle', 'Pick a direction')}
        </h2>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {t(
            'chaptersFirst.pickOptionHint',
            'Three takes on the same brief. Pick one to start editing.'
          )}
        </p>
      </div>
      {applyError && <p className="text-sm text-red-600">{applyError}</p>}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {options.map((option) => (
          <OptionCard
            key={option.id}
            option={option}
            isExpanded={expandedOptionId === option.id}
            isApplying={applyingOptionId === option.id}
            anyApplying={applyingOptionId !== null}
            onToggleExpand={() => onToggleExpand(option.id)}
            onPick={() => onPick(option)}
            t={t}
          />
        ))}
      </div>
    </div>
  );
}

type OptionCardProps = {
  option: ChapterFlowOption;
  isExpanded: boolean;
  isApplying: boolean;
  anyApplying: boolean;
  onToggleExpand: () => void;
  onPick: () => void;
  t: TFunction;
};

function OptionCard({
  option,
  isExpanded,
  isApplying,
  anyApplying,
  onToggleExpand,
  onPick,
  t,
}: OptionCardProps) {
  const totalChapters = option.chapters.length;
  const previewChapters = isExpanded ? option.chapters : option.chapters.slice(0, PREVIEW_CHAPTERS);
  const hiddenCount = totalChapters - previewChapters.length;

  return (
    <Card padding="lg">
      <div className="flex flex-col h-full gap-3">
        <div>
          <h3 className="font-semibold text-gray-900 dark:text-gray-100">{option.label}</h3>
          {option.summary && (
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{option.summary}</p>
          )}
        </div>

        <ol className="space-y-2 text-sm">
          {previewChapters.map((chapter, idx) => (
            <li key={`${option.id}-${idx}`}>
              <div className="font-medium text-gray-800 dark:text-gray-200">
                <span className="text-gray-400 mr-1.5">{idx + 1}.</span>
                {chapter.title}
              </div>
              {isExpanded && chapter.covers.length > 0 && (
                <ul className="mt-1 ml-5 space-y-0.5 list-disc list-outside pl-3 text-xs text-gray-600 dark:text-gray-400">
                  {chapter.covers.map((cover, ci) => (
                    <li key={ci}>{cover}</li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ol>

        {hiddenCount > 0 && (
          <button
            type="button"
            onClick={onToggleExpand}
            className="self-start text-xs font-medium text-primary-600 dark:text-primary-400 hover:underline"
          >
            {t('chaptersFirst.showMoreChapters', 'Show all {{count}} chapters', {
              count: totalChapters,
            })}
          </button>
        )}
        {hiddenCount === 0 && isExpanded && totalChapters > PREVIEW_CHAPTERS && (
          <button
            type="button"
            onClick={onToggleExpand}
            className="self-start text-xs font-medium text-gray-500 dark:text-gray-400 hover:underline"
          >
            {t('chaptersFirst.showLessChapters', 'Show less')}
          </button>
        )}

        <div className="mt-auto pt-2">
          <Button onClick={onPick} isLoading={isApplying} disabled={anyApplying} className="w-full">
            {t('chaptersFirst.useThisStructure', 'Use this structure')}
          </Button>
        </div>
      </div>
    </Card>
  );
}

type CoverRowProps = {
  value: string;
  placeholder: string;
  onChange: (value: string) => void;
  onEnter: () => void;
  onBackspaceEmpty: () => void;
  onBlur: () => void;
  registerRef: (el: HTMLTextAreaElement | null) => void;
};

function CoverRow({
  value,
  placeholder,
  onChange,
  onEnter,
  onBackspaceEmpty,
  onBlur,
  registerRef,
}: CoverRowProps) {
  const ref = useRef<HTMLTextAreaElement | null>(null);

  const resize = () => {
    const el = ref.current;
    if (!el) return;
    el.style.height = '0px';
    el.style.height = `${el.scrollHeight}px`;
  };

  useLayoutEffect(() => {
    resize();
  }, [value]);

  return (
    <textarea
      ref={(el) => {
        ref.current = el;
        registerRef(el);
      }}
      value={value}
      rows={1}
      onChange={(e) => {
        onChange(e.target.value);
        // Resize on next frame after the value lands in the DOM
        requestAnimationFrame(resize);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          onEnter();
        } else if (e.key === 'Backspace' && value.length === 0) {
          e.preventDefault();
          onBackspaceEmpty();
        }
      }}
      onBlur={onBlur}
      placeholder={placeholder}
      className="block flex-1 min-w-0 w-full bg-transparent text-sm text-gray-800 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 border-b border-transparent hover:border-gray-200 dark:hover:border-gray-700 focus:border-primary-400 dark:focus:border-primary-500 focus:outline-none py-1 resize-none leading-relaxed overflow-hidden whitespace-pre-wrap break-words"
    />
  );
}

function PencilIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
      />
    </svg>
  );
}

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
  );
}

function SparkleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 20 20" aria-hidden>
      <path d="M10 1.5l1.6 4.4 4.4 1.6-4.4 1.6L10 13.5 8.4 9.1 4 7.5l4.4-1.6L10 1.5zM15 12l.8 2.2 2.2.8-2.2.8L15 18l-.8-2.2L12 15l2.2-.8L15 12zM4.5 13l.6 1.7 1.7.6-1.7.6L4.5 17.6 3.9 15.9 2.2 15.3l1.7-.6L4.5 13z" />
    </svg>
  );
}

function SlidersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M4 6h16M4 12h10M4 18h7M18 10v4M14 16v4M20 4v4"
      />
    </svg>
  );
}
